import type { FC } from "react";
import { capitalize } from "@lib/utils/commonUtility";
import type { BreadcrumbProps } from "@ui/components/navigation/breadcrumb/Breadcrumb";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
} from "@ui/components/navigation/breadcrumb/Breadcrumb";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { PDPSeoBreadcrumbSchema } from "@components/seo/PDPSeoBreadcrumbSchema";
import { BREADCRUMBS_ITEM, BREADCRUMBS_WRAPPER } from "@e2e/helpers/common/constants/common";

interface IBreadcrumbsProps extends BreadcrumbProps {
    items: { label: string; link: string }[];
    shouldExcludeLastItem?: boolean;
    isPDP?: boolean;
}

export const Breadcrumbs: FC<IBreadcrumbsProps> = ({
    items,
    shouldExcludeLastItem = true,
    color,
    ...rest
}) => {
    const { get: getMicrocopy } = useMicrocopy();

    return (
        <>
            <PDPSeoBreadcrumbSchema breadcrumbsData={items} />
            <Breadcrumb
                aria-label={
                    getMicrocopy(CommonCMS.shared, CommonCMS.ariaLabels.breadcrumbAria) ||
                    "Breadcrumbs"
                }
                data-testid={BREADCRUMBS_WRAPPER}
                {...rest}
            >
                {items.map((item, index) => {
                    const isLastItem = index === items.length - 1;
                    const itemLabel = capitalize(item.label);

                    const linkProps = {
                        href: isLastItem && !shouldExcludeLastItem ? "" : item.link,
                        sx: {
                            color: color,
                            fontSize: ["mobileLinkL1", "desktopLinkL1"],
                            _hover: {
                                textDecoration: "underline",
                            },
                            ...(isLastItem &&
                                !shouldExcludeLastItem && {
                                    pointerEvents: "none",
                                }),
                        },
                    };

                    return (
                        <BreadcrumbItem
                            key={item.label + index}
                            color={color}
                            data-testid={BREADCRUMBS_ITEM}
                        >
                            <BreadcrumbLink {...linkProps}>{itemLabel}</BreadcrumbLink>
                        </BreadcrumbItem>
                    );
                })}
            </Breadcrumb>
        </>
    );
};
